import type { SceneryArrays } from './types';
import type { PlacedShortcut } from '@/physics/shortcuts';

/**
 * scenery/shortcutClear.ts — the SECRET-SHORTCUT CORRIDOR CLEARING (M116b,
 * pure). Same doctrine as the M97 finish clearing: zone builders know nothing
 * about the shortcuts, so on some seeds a brownstone stands square on a portal
 * mouth or a hill bulges through the boardwalk (measured live on a random
 * quick-race track — the portal vanished inside a building). Rather than touch
 * the builders (their rng SEQUENCE is each city's layout), this runs AFTER
 * them and deletes offending instances — determinism preserved, every other
 * placement byte-identical.
 *
 * Three rules:
 *  - MOUTHS: nothing vertical inside a portal's forecourt (the doorway, its
 *    dressing, and the player's approach sight-line).
 *  - THE CORRIDOR: nothing vertical standing ON the transit path itself —
 *    the bow deliberately carves through the city block, so the bore/alley/
 *    boardwalk shell needs its own right-of-way.
 *  - EDGES: hills and tree canopies whose RADIUS crosses the corridor are
 *    dropped even when their centre sits clear (the M97/M98 edge rule).
 *
 * Ground sheets, water and people deliberately survive — a jetty over the
 *  lagoon and a crowd by a subway stair are the point, not a bug.
 */

/** Instance arrays swept by the corridor rule (mirrors M97's VERTICAL_KEYS —
 *  everything that stands UP). */
const VERTICAL_KEYS = [
  'buildings',
  'plain',
  'adBuildings',
  'roofs',
  'neon',
  'adScreens',
  'vertSigns',
  'trees',
  'palms',
  'pines',
  'baobabs',
  'stalls',
  'umbrellas',
  'scooters',
  'cabs',
  'buses',
  'phoneBoxes',
  'stiltHouses',
  'lanterns',
  'laundry',
  'fences',
  'saltMounds',
] as const;

/** Forecourt radius around each mouth. M117c: SHRUNK on purpose — the deck
 *  entrances should hide among the scenery now (owner rule), so trees and
 *  buildings may crowd close; only what would stand ON the landing goes. */
const MOUTH_CLEAR = 14;
/** Right-of-way half-width around the transit path (shell ≈ 3.4 + the worst
 *  building half-extent + breathing room). */
const CORRIDOR_CLEAR = 14;

/** Squared distance from (x,z) to the nearest SURFACE point of the channel
 *  (M117: only spine stretches at/above grade need right-of-way — a tunnel's
 *  deep bore keeps the city block standing over it, which is both the
 *  disguise and the fiction). */
function corridorDist2(sc: PlacedShortcut, x: number, z: number): number {
  let best = Infinity;
  for (const pt of sc.surfacePts) {
    const dx = x - pt.x;
    const dz = z - pt.z;
    const d = dx * dx + dz * dz;
    if (d < best) best = d;
  }
  return best;
}

/** True when a point stands in any shortcut's right-of-way. `extra` widens the
 *  band for radius-carrying instances (hills, canopies). */
function inCorridor(shortcuts: readonly PlacedShortcut[], x: number, z: number, extra = 0): boolean {
  for (const sc of shortcuts) {
    const mouth = MOUTH_CLEAR + extra;
    const em = (x - sc.door.x) ** 2 + (z - sc.door.z) ** 2;
    if (em < mouth * mouth) return true;
    const xm = (x - sc.exitDoor.x) ** 2 + (z - sc.exitDoor.z) ** 2;
    if (xm < mouth * mouth) return true;
    const band = CORRIDOR_CLEAR + extra;
    if (corridorDist2(sc, x, z) < band * band) return true;
  }
  return false;
}

/** Clear every shortcut's right-of-way. Mutates `a` in place (build time). */
export function clearShortcutCorridors(
  a: SceneryArrays,
  shortcuts: readonly PlacedShortcut[],
): void {
  if (!shortcuts.length) return;

  for (const key of VERTICAL_KEYS) {
    const list = a[key];
    for (let i = list.length - 1; i >= 0; i--) {
      const inst = list[i] as { x: number; z: number };
      if (inCorridor(shortcuts, inst.x, inst.z)) list.splice(i, 1);
    }
  }

  // Edge rules: an instance's REACH matters, not just its centre.
  for (let i = a.hills.length - 1; i >= 0; i--) {
    const h = a.hills[i];
    if (inCorridor(shortcuts, h.x, h.z, h.r)) a.hills.splice(i, 1);
  }
  const canopyLists = [a.trees, a.palms, a.pines, a.baobabs] as const;
  for (const list of canopyLists) {
    for (let i = list.length - 1; i >= 0; i--) {
      const t = list[i] as { x: number; z: number; s?: number };
      if (inCorridor(shortcuts, t.x, t.z, 4 * (t.s ?? 1))) list.splice(i, 1);
    }
  }
}
